import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  List,
  ListItem,
  ListItemText,
  Divider,
  alpha,
  CircularProgress,
  Chip
} from '@mui/material';

//import feedbackJsonData from './strengths-opportunities.json';
import Flower from '../Tree/components/Flower';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { COLORS } from '../Tree/constants/theme';
const TURN_GAP_THRESHOLD = 1.5;

const CATEGORY_COLORS = {
  learning_community: COLORS.categoryLearningCommunity,
  content_knowledge: COLORS.categoryContentKnowledge,
  rigorous_thinking: COLORS.categoryRigorousThinking,
  improvement: COLORS.categoryImprovementFallback,
  default: COLORS.categoryDefault
};

const FLOWER_COLORS = {
  'Rigorous Thinking': 'redflower',
  'Content Knowledge': 'blueflower',
  'Learning Community': 'yellowflower',
  default: 'greenflower'
};

const CATEGORY_ORDER_MAP = {
  content_knowledge: 1,
  learning_community: 2,
  rigorous_thinking: 3
};

const CATEGORY_LABEL_MAP = {
  rigorous_thinking: 'Pressing for Reasoning',
  content_knowledge: 'Pressing for Accuracy',
  learning_community: 'Getting Students to Relate'
};

const ICON_SIZES = {
  flower: 24,
  leaf: 24,
  container: 52,
  border: 2,
  padding: 0.5
};

const TYPOGRAPHY = {
  title: { fontSize: '20px', fontWeight: 600 },
  instruction: { fontSize: '14px', fontWeight: 400 },
  categoryLabel: { fontSize: '0.9rem', fontWeight: 600 },
  transcriptText: { fontSize: '0.9rem', fontWeight: 400 },
  feedbackTitle: { fontSize: '16px', fontWeight: 600 },
  feedbackText: { fontSize: '0.95rem', fontWeight: 400 },
  chip: { fontSize: '0.85rem', fontWeight: 500 }
};

const ACORN_BROWN = COLORS.acornBrown;

const ExploreMyTalk = ({
  selectedTreeImage,
  processTranscriptData,
  selectedTreeId,
  sessionData,
}) => {

  const [transcript, setTranscript] = useState([]);
  const transcriptListRef = useRef(null);
  const [hasSelectedTree, setHasSelectedTree] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [feedbackData, setFeedbackData] = useState([]);
  const [selectedFeedbackKey, setSelectedFeedbackKey] = useState(null);
  const [categoriesWithFeedback, setCategoriesWithFeedback] = useState([]);

  // Utility functions
  const toTitleCase = useCallback((str) =>
    str
      ? str
        .replace(/_/g, ' ')
        .replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase())
      : '', []);

  const normalizeText = useCallback((text) => {
    if (Array.isArray(text)) {
      return text.join(' ');
    }
    return text || '';
  }, []);

  const anonymizeItemText = useCallback((text, participantMap, participantPrefixMap) => {
    if (!text || !participantMap || !participantPrefixMap) {
      return text || '';
    }

    let anonymizedText = text;

    for (const [participantId, [type, name]] of Object.entries(participantMap)) {
      if (name && participantPrefixMap[participantId]) {
        const nameRegex = new RegExp(`\\b${name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'gi');
        anonymizedText = anonymizedText.replace(nameRegex, participantPrefixMap[participantId]);
      }
    }

    return anonymizedText;
  }, []);

  const getCategoryFlowerColor = useCallback((label) => {
    return FLOWER_COLORS[label] || FLOWER_COLORS.default;
  }, []);

  const getCategoryColor = useCallback((classification) => {
    return CATEGORY_COLORS[classification] || CATEGORY_COLORS.default;
  }, []);

  const groupIntoTurns = useCallback((transcriptData) => {
    if (!transcriptData || transcriptData.length === 0) return [];

    const turns = [];
    let currentTurn = null;

    transcriptData.forEach((item) => {
      const timeGap = currentTurn ? (item.start - currentTurn.end) : 0;

      const shouldStartNewTurn =
        !currentTurn ||
        currentTurn.speaker !== item.speaker ||
        timeGap >= TURN_GAP_THRESHOLD;

      if (shouldStartNewTurn) {
        if (currentTurn) {
          turns.push(currentTurn);
        }
        currentTurn = {
          speaker: item.speaker,
          speakerName: item.speakerName,
          speakerType: item.speakerType,
          start: item.start,
          end: item.end,
          startIndex: item.index,
          endIndex: item.index,
          texts: [item.text],
          utterances: [item]
        };
      } else {
        currentTurn.texts.push(item.text);
        currentTurn.utterances.push(item);
        currentTurn.endIndex = item.index;
        currentTurn.end = item.end;
      }
    });

    if (currentTurn) {
      turns.push(currentTurn);
    }

    return turns;
  }, []);

  const turns = useMemo(() => groupIntoTurns(transcript), [transcript, groupIntoTurns]);

  const getAdjacentFeedbackKeys = useCallback(() => {
    if (!selectedFeedbackKey || categoriesWithFeedback.length === 0) {
      return { prev: null, next: null };
    }

    const allKeys = [];
    categoriesWithFeedback.forEach(category => {
      if (category.strength) {
        allKeys.push(`${category.classification}-strength`);
      }
      if (category.improvement) {
        allKeys.push(`${category.classification}-improvement`);
      }
    });

    const currentIndex = allKeys.indexOf(selectedFeedbackKey);

    return {
      prev: currentIndex > 0 ? allKeys[currentIndex - 1] : null,
      next: currentIndex < allKeys.length - 1 ? allKeys[currentIndex + 1] : null
    };
  }, [selectedFeedbackKey, categoriesWithFeedback]);

  const getSelectedFeedback = useCallback(() => {
    if (!selectedFeedbackKey) return null;

    const [classification, sentiment] = selectedFeedbackKey.split('-');
    const category = categoriesWithFeedback.find(c => c.classification === classification);

    if (!category) return null;
    return sentiment === 'strength' ? category.strength : category.improvement;
  }, [selectedFeedbackKey, categoriesWithFeedback]);

  useEffect(() => {
    if (!Array.isArray(feedbackData) || feedbackData.length === 0) {
      //console.log("no feedback data in effect");
      setCategoriesWithFeedback([]);
      setSelectedFeedbackKey(null);
      return;
    }

    const classificationMap = {};

    feedbackData.forEach((item) => {
      // console.log("processing feedback item:", item);
      const classification = item.classification;
      if (!classification) {
        console.warn('Item missing classification:', item);
        return;
      }

      if (!classificationMap[classification]) {
        classificationMap[classification] = {
          classification,
          label: toTitleCase(classification),
          strength: null,
          improvement: null
        };
      }

      const normalizedItem = {
        ...item,
        text: normalizeText(item.text)
      };

      if (item.sentiment === 'strength' && !classificationMap[classification].strength) {
        classificationMap[classification].strength = normalizedItem;
      } else if (item.sentiment === 'improvement' && !classificationMap[classification].improvement) {
        classificationMap[classification].improvement = normalizedItem;
      }
    });

    const categories = Object.values(classificationMap);

    const sortedCategories = categories.sort((a, b) => {
      const orderA = CATEGORY_ORDER_MAP[a.classification] || 999;
      const orderB = CATEGORY_ORDER_MAP[b.classification] || 999;
      return orderA - orderB;
    });

    setCategoriesWithFeedback(sortedCategories);

    let firstKey = null;
    for (const category of categories) {
      if (category.strength) {
        firstKey = `${category.classification}-strength`;
        break;
      } else if (category.improvement) {
        firstKey = `${category.classification}-improvement`;
        break;
      }
    }

    setSelectedFeedbackKey(firstKey);
  }, [feedbackData, toTitleCase, normalizeText]);

  const formatTranscriptData = useCallback((processedData, participantMap) => {
    const participantPrefixMap = {};
    let studentCount = 1;

    for (const [id, [type]] of Object.entries(participantMap)) {
      if (type === 'student') {
        participantPrefixMap[id] = `S${studentCount++}`;
      } else if (type === 'tutor' || type === 'instructor') {
        participantPrefixMap[id] = `T`;
      }
    }

    return processedData.map((item, index) => ({
      start: item.start || 0,
      end: item.end || 0,
      speaker: item.participantId || 'Unknown',
      speakerName: participantMap[item.participantId] || item.participantId || 'Unknown',
      speakerType: item.speakerType || 'unknown',
      speakerPrefix: participantPrefixMap[item.participantId] || 'U',
      text: anonymizeItemText(item.text, participantMap, participantPrefixMap),
      talkMoveType: item.talkMoveType || null,
      index: index
    }));
  }, [anonymizeItemText]);

  useEffect(() => {
    const loadTranscript = async () => {
      setTranscript([]);

      if (!selectedTreeId || !processTranscriptData) {
        setHasSelectedTree(false);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);

      try {
        const data = await processTranscriptData(selectedTreeId);

        if (data && data.processedData) {
          const participantMap = {};
          if (data.sessionMetadata && data.sessionMetadata.participants) {
            data.sessionMetadata.participants.forEach(p => {
              participantMap[p.id] = [p.type, p.name];
            });
          }

          const formattedTranscript = formatTranscriptData(data.processedData, participantMap);

          setTranscript(formattedTranscript);
          setHasSelectedTree(true);
          mixpanel.track('exploreMyTalkSessionViewed', {
            sessionId: selectedTreeId,
            utteranceCount: formattedTranscript.length,
          });
        }
      } catch (error) {
        console.error('Error loading transcript:', error);
        mixpanel.track('exploreMyTalkTranscriptLoadFailed', {
          sessionId: selectedTreeId,
          error: error.message || 'Unknown error',
        });
        setTranscript([]);
        setHasSelectedTree(false);
      } finally {
        setIsLoading(false);
      }
    };

    loadTranscript();
  }, [selectedTreeId, processTranscriptData, formatTranscriptData]);

  useEffect(() => {
    setTranscript([]);
    setFeedbackData([]);
    setSelectedFeedbackKey(null);
    setCategoriesWithFeedback([]);
  }, [selectedTreeId]);

  const filterSessionFeedback = useCallback((feedbackArray) => {
    // reduce the large quanity of feedback to one strength and one improvement for each
    // of three target classifications (talk move types).
    // Also update those classification values to their talk move categories
    if (!feedbackArray || !Array.isArray(feedbackArray)) return [];

    const targetClassifications = ['pressing_for_accuracy', 'pressing_for_reasoning', 'getting_students_to_relate'];
    const classificationMap = {
      'pressing_for_accuracy': 'content_knowledge',
      'pressing_for_reasoning': 'rigorous_thinking',
      'getting_students_to_relate': 'learning_community'
    };
    const filteredFeedback = [];

    const pickHighestRanked = (items) => {
      if (items.length === 0) return null;
      return items.reduce((best, item) => {
        const bestRank = typeof best.rank === 'number' ? best.rank : Infinity;
        const itemRank = typeof item.rank === 'number' ? item.rank : Infinity;
        return itemRank < bestRank ? item : best;
      });
    };

    targetClassifications.forEach(classification => {
      // Get all items for this classification
      const classificationItems = feedbackArray.filter(item => item.classification === classification);

      // Get strength items and select the highest-ranked one
      const strengthItems = classificationItems.filter(item => item.sentiment === 'strength');
      const bestStrength = pickHighestRanked(strengthItems);
      if (bestStrength) {
        filteredFeedback.push(bestStrength);
      }

      // Get improvement items and select the highest-ranked one
      const improvementItems = classificationItems.filter(item => item.sentiment === 'improvement');
      const bestImprovement = pickHighestRanked(improvementItems);
      if (bestImprovement) {
        filteredFeedback.push(bestImprovement);
      }
    });

    // for each filtered item, remove the "Usefulness = N" that is appended to the text, where N is 1-5
    const usefulnessRegex = / Usefulness = [1-5]$/;

    return filteredFeedback.map(item => ({
      ...item,
      text: item.text.replace(usefulnessRegex, ''),
      classification: classificationMap[item.classification] || item.classification
    }));
  }, []);

  useEffect(() => {
    if (selectedTreeId && sessionData?.strengthsOpportunitiesFeedback?.feedback) {
      console.log('RAW feedback data:', sessionData.strengthsOpportunitiesFeedback.feedback);
      const filteredFeedback = filterSessionFeedback(sessionData.strengthsOpportunitiesFeedback.feedback);
      setFeedbackData(filteredFeedback);
      console.log('Set filtered session feedback data.')
      //console.log('filtered feedback is:', filteredFeedback);
    } else {
      console.log("No strengthsOpportunitiesFeedback for selectedTreeId:", selectedTreeId);
      // console.log("Session Data:", sessionData);
      setFeedbackData([]);
      setTranscript([]);
      setSelectedFeedbackKey(null);
      setCategoriesWithFeedback([]);
    }
  }, [selectedTreeId, sessionData, filterSessionFeedback]);

  useEffect(() => {
    const feedback = getSelectedFeedback();
    if (!feedback || !feedback.contexts || feedback.contexts.length === 0) return;

    const firstContext = feedback.contexts[0];
    const targetIndex = firstContext.startIndex;

    if (transcriptListRef.current && turns.length > 0) {
      setTimeout(() => {
        const targetTurn = turns.find(turn =>
          targetIndex >= turn.startIndex && targetIndex <= turn.endIndex
        );

        if (targetTurn) {
          const listItems = transcriptListRef.current.querySelectorAll('[data-turn-index]');
          const targetItem = Array.from(listItems).find(
            item => parseInt(item.getAttribute('data-turn-start-index')) === targetTurn.startIndex
          );

          if (targetItem) {
            const transcriptContainer = transcriptListRef.current;
            const containerRect = transcriptContainer.getBoundingClientRect();
            const itemRect = targetItem.getBoundingClientRect();

            const scrollOffset = itemRect.top - containerRect.top + transcriptContainer.scrollTop;

            transcriptContainer.scrollTo({
              top: scrollOffset,
              behavior: 'smooth'
            });
          }
        }
      }, 100);
    }
  }, [selectedFeedbackKey, turns, getSelectedFeedback]);

  const EmptyOrLoadingState = ({ message, showLoading = false }) => (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '200px',
      p: 3
    }}
      role="status"
      aria-live="polite"
      aria-busy={showLoading}>
      {showLoading && <CircularProgress sx={{ mb: 2 }} aria-label="Loading content" />}
      <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center' }}>
        {message}
      </Typography>
    </Box>
  );

  // Outer range (segmentStartIndex/segmentEndIndex): determines the border
  // strip and whether a turn is dimmed at all.
  const isTurnInSelectedRange = useCallback((turn) => {
    const feedback = getSelectedFeedback();
    if (!feedback || !feedback.contexts) return false;

    return feedback.contexts.some(context => {
      if (context.segmentStartIndex == null || context.segmentEndIndex == null) return false;
      return !(turn.endIndex < context.segmentStartIndex || turn.startIndex > context.segmentEndIndex);
    });
  }, [getSelectedFeedback]);

  // Inner range (startIndex/endIndex), nested inside the segment range:
  // determines the solid light-fill background on that specific turn.
  const isTurnInInnerContext = useCallback((turn) => {
    const feedback = getSelectedFeedback();
    if (!feedback || !feedback.contexts) return false;

    return feedback.contexts.some(context => {
      if (context.startIndex == null || context.endIndex == null) return false;
      return !(turn.endIndex < context.startIndex || turn.startIndex > context.endIndex);
    });
  }, [getSelectedFeedback]);

  const getBorderColor = useCallback((selectedFeedback, inRange) => {
    if (!inRange || !selectedFeedback) return 'transparent';
    return getCategoryColor(selectedFeedback.classification);
  }, [getCategoryColor]);

  const selectedFeedback = getSelectedFeedback();

  const _sessionTitle = (sessionData?.sessionTitle || sessionData?.sessionMetadata?.title || '').toLowerCase();
  const isSampleSession = _sessionTitle.startsWith('sample session') || _sessionTitle.startsWith('sample tree');

  return (
    <Box sx={{ height: '45rem', display: 'flex', flexDirection: 'column', bgcolor: COLORS.explorePageBg }}
      role="region"
      aria-label="Session feedback and transcript viewer">
      <Box sx={{ flex: 1, overflow: 'hidden', p: 2 }}>
        {!selectedTreeId ? (
          <Paper elevation={0} sx={{
            p: 4,
            textAlign: 'center',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            bgcolor: COLORS.white
          }}
            role="status"
            aria-live="polite">
            <Typography variant="h4" color="text.secondary">
              Please select a talk tree above to view <br />the session transcript and feedback.
            </Typography>
          </Paper>
        ) : isLoading ? (
          <Paper elevation={0} sx={{
            p: 4,
            textAlign: 'center',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            bgcolor: COLORS.white
          }}
            role="status"
            aria-live="polite"
            aria-busy="true">
            <CircularProgress sx={{ mb: 2 }} aria-label="Loading session data" />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Loading Session Data...
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Please wait while we fetch the transcript and feedback
            </Typography>
          </Paper>
        ) : (
          <Grid container spacing={2} sx={{
            height: '100%',
            overflow: 'auto',
            flexWrap: { xs: 'wrap', md: 'nowrap' }
          }}>
            {/* Left Column - Feedback Category Selection */}
            <Grid item xs={12} md={2.5} sx={{
              height: { xs: 'auto', md: '100%' },
              display: 'flex',
              flexDirection: 'column',
              minWidth: '300px'
            }}>
              {/* Tree Image */}
              <Paper elevation={0} sx={{ p: 2, bgcolor: COLORS.white, maxHeight: '300px' }}>
                <Typography variant="h2" sx={{
                  fontSize: '20px',
                  fontWeight: 600,
                  mb: 0.5,
                  textAlign: 'left'
                }}>
                  Session
                </Typography>
                <Typography variant="body1" sx={{
                  fontSize: '14px',
                  fontWeight: 600,
                  mb: 1,
                  textAlign: 'left',
                  color: 'text.secondary'
                }}>
                  {sessionData?.sessionTitle || sessionData?.sessionMetadata?.title || 'Untitled Session'}
                </Typography>
                {selectedTreeImage ? (
                  <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    border: `1px solid ${COLORS.feedbackCardBorder}`,
                    borderRadius: 1,
                    p: 2,
                    bgcolor: COLORS.feedbackCardBg,
                    maxHeight: '200px'
                  }}>
                    <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', mb: 1 }}>
                      {(() => {
                        if (sessionData?.startTime) {
                          const date = new Date(sessionData.startTime);
                          const options = { weekday: 'long', month: 'long', day: 'numeric' };
                          return date.toLocaleDateString(undefined, options);
                        }
                        return '';
                      })()}
                    </Typography>
                    <img
                      src={selectedTreeImage}
                      alt="Talk tree visualization showing student engagement patterns for selected session"
                      style={{
                        maxWidth: '100%',
                        height: 'auto',
                        maxHeight: '180px',
                        objectFit: 'contain'
                      }}
                    />
                  </Box>
                ) : (
                  <Box sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '150px',
                    border: `1px dashed ${COLORS.feedbackCardBorder}`,
                    borderRadius: 1,
                    bgcolor: COLORS.feedbackCardBg
                  }}>
                    <Typography variant="body2" color="text.secondary">
                      No tree image
                    </Typography>
                  </Box>
                )}
              </Paper>

              {/* Feedback Categories */}
              <Paper elevation={0} sx={{ p: 2, mb: 2, bgcolor: COLORS.white, flex: 1, overflow: 'auto' }}
                role="region"
                aria-label="Feedback categories">
                <Typography variant="subtitle1" sx={{ ...TYPOGRAPHY.instruction, mb: 2, lineHeight: 1.8, textAlign: 'left', display: 'flex', flexDirection: 'column', gap: 0.5, backgroundColor: COLORS.studentRowBg, padding: '8px 12px', borderRadius: '4px' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    Click
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', width: 14, height: 14, transform: 'scale(0.7)', transformOrigin: 'left', mr: 1 }}>
                      <Flower color={COLORS.categoryContentKnowledge} />
                    </Box>
                    to see a Strength or
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                      <svg width="20" height="30" viewBox="0 0 90 110" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <g transform="translate(45, 50) rotate(-30) translate(-45, -45)">
                          <path d="M 68.088 66.685 C 42.441 92.332 26.759 89.373 5.407 81.712 c -7.661 -21.352 -10.62 -37.035 15.027 -62.681 C 42.278 29.721 58.295 45.49 68.088 66.685 z" fill={COLORS.acornCap} />
                          <path d="M 74.939 21.703 l -9.802 -9.802 c -12.027 -12.027 -31.527 -12.027 -43.554 0 l 0 0 c -2.286 2.286 -2.286 5.993 0 8.279 L 66.66 65.257 c 2.286 2.286 5.993 2.286 8.279 0 l 0 0 C 86.966 53.23 86.966 33.73 74.939 21.703 z" fill={ACORN_BROWN} />
                          <path d="M 71.529 18.294 c 5.482 -3.916 11.757 -5.109 18.471 -4.802 l -5.474 -5.474 c -6.117 -0.696 -10.381 2.724 -15.701 7.571 C 67.923 17.426 68.835 18.322 71.529 18.294 z" fill={ACORN_BROWN} />
                        </g>
                      </svg>
                    </Box>
                    to see an Opportunity
                  </Box>
                </Typography>

                {categoriesWithFeedback.length > 0 ? (
                  <Box sx={{ width: '100%' }} role="list" aria-label="Feedback categories list">
                    {categoriesWithFeedback.map((category) => (
                      <Paper
                        key={category.classification}
                        elevation={0}
                        sx={{
                          mb: 1,
                          p: 1.5,
                          border: '1px solid',
                          borderColor: COLORS.white,
                          borderRadius: 1,
                          bgcolor: COLORS.white,
                        }}
                        role="listitem"
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                          <Typography
                            variant="subtitle2"
                            sx={TYPOGRAPHY.categoryLabel}
                          >
                            {category.label}
                          </Typography>

                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', width: `${ICON_SIZES.container}px`, justifyContent: 'flex-start' }}>
                              <Box
                                sx={{
                                  width: `${ICON_SIZES.flower}px`,
                                  height: `${ICON_SIZES.flower}px`,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  flexShrink: 0
                                }}
                              >
                                {category.strength && (
                                  <Box
                                    onClick={() => {
                                      setSelectedFeedbackKey(`${category.classification}-strength`);
                                      mixpanel.track('exploreMyTalkFeedbackSelected', { classification: category.classification, sentiment: 'strength', source: 'flower_click' });
                                    }}
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        setSelectedFeedbackKey(`${category.classification}-strength`);
                                        mixpanel.track('exploreMyTalkFeedbackSelected', { classification: category.classification, sentiment: 'strength', source: 'flower_click' });
                                      }
                                    }}
                                    aria-label={`View ${category.label} strength feedback`}
                                    aria-pressed={selectedFeedbackKey === `${category.classification}-strength`}
                                    sx={{
                                      cursor: 'pointer',
                                      p: ICON_SIZES.padding,
                                      borderRadius: '8px',
                                      border: selectedFeedbackKey === `${category.classification}-strength`
                                        ? `${ICON_SIZES.border}px solid ${getCategoryColor(category.classification)}`
                                        : `${ICON_SIZES.border}px solid transparent`,
                                      backgroundColor: 'transparent',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      minWidth: `${ICON_SIZES.flower}px`,
                                      height: `${ICON_SIZES.flower}px`,
                                      '&:hover': {
                                        border: `${ICON_SIZES.border}px solid ${getCategoryColor(category.classification)}`,
                                      },
                                      transition: 'border-color 0.2s'
                                    }}
                                  >
                                    <Box sx={{ transform: 'scale(0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                      <Flower
                                        flowerId={`flower-${category.classification}-strength`}
                                        color={getCategoryFlowerColor(category.label)}
                                      />
                                    </Box>
                                  </Box>
                                )}
                              </Box>

                              <Box
                                sx={{
                                  width: `${ICON_SIZES.leaf}px`,
                                  height: `${ICON_SIZES.leaf}px`,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  flexShrink: 0
                                }}
                              >
                                {category.improvement && (
                                  <Box
                                    onClick={() => {
                                      setSelectedFeedbackKey(`${category.classification}-improvement`);
                                      mixpanel.track('exploreMyTalkFeedbackSelected', { classification: category.classification, sentiment: 'improvement', source: 'leaf_click' });
                                    }}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        setSelectedFeedbackKey(`${category.classification}-improvement`);
                                        mixpanel.track('exploreMyTalkFeedbackSelected', { classification: category.classification, sentiment: 'improvement', source: 'leaf_click' });
                                      }
                                    }}
                                    role="button"
                                    tabIndex={0}
                                    aria-label={`View ${category.label} opportunity feedback`}
                                    aria-pressed={selectedFeedbackKey === `${category.classification}-improvement`}
                                    sx={{
                                      cursor: 'pointer',
                                      p: ICON_SIZES.padding,
                                      borderRadius: '8px',
                                      border: selectedFeedbackKey === `${category.classification}-improvement`
                                        ? `${ICON_SIZES.border}px solid ${getCategoryColor(category.classification)}`
                                        : `${ICON_SIZES.border}px solid transparent`,
                                      backgroundColor: 'transparent',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      width: `${ICON_SIZES.leaf}px`,
                                      height: `${ICON_SIZES.leaf}px`,
                                      '&:hover': {
                                        border: `${ICON_SIZES.border}px solid ${getCategoryColor(category.classification)}`,
                                      },
                                      transition: 'border-color 0.2s'
                                    }}
                                  >
                                    <svg width="20" height="23" viewBox="0 0 90 110" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      <g transform="translate(45, 50) rotate(-30) translate(-45, -45)">
                                        <path d="M 68.088 66.685 C 42.441 92.332 26.759 89.373 5.407 81.712 c -7.661 -21.352 -10.62 -37.035 15.027 -62.681 C 42.278 29.721 58.295 45.49 68.088 66.685 z" fill={getCategoryColor(category.classification)} />
                                        <path d="M 74.939 21.703 l -9.802 -9.802 c -12.027 -12.027 -31.527 -12.027 -43.554 0 l 0 0 c -2.286 2.286 -2.286 5.993 0 8.279 L 66.66 65.257 c 2.286 2.286 5.993 2.286 8.279 0 l 0 0 C 86.966 53.23 86.966 33.73 74.939 21.703 z" fill={ACORN_BROWN} />
                                        <path d="M 71.529 18.294 c 5.482 -3.916 11.757 -5.109 18.471 -4.802 l -5.474 -5.474 c -6.117 -0.696 -10.381 2.724 -15.701 7.571 C 67.923 17.426 68.835 18.322 71.529 18.294 z" fill={ACORN_BROWN} />
                                      </g>
                                    </svg>
                                  </Box>
                                )}
                              </Box>
                            </Box>
                          </Box>
                        </Box>
                      </Paper>
                    ))}
                  </Box>
                ) : (
                  <EmptyOrLoadingState message="No feedback available for this session" />
                )}
              </Paper>
            </Grid>

            {/* Middle Column - Transcript */}
            <Grid item xs={12} md={4.5} sx={{
              height: { xs: '500px', md: '100%' },
              minWidth: '250px'
            }}>
              <Paper elevation={0} sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: COLORS.white }}
                role="region"
                aria-label="Session transcript">
                <Box sx={{ px: 2, pt: 2, pb: 1, borderBottom: `1px solid ${COLORS.feedbackCardBorder}` }}>
                  <Typography variant="subtitle1" sx={{
                    ...TYPOGRAPHY.title,
                    textAlign: 'left'
                  }}>
                    Transcript
                  </Typography>
                </Box>

                <List
                  ref={transcriptListRef}
                  sx={{
                    flex: 1,
                    overflow: 'auto',
                    py: 0,
                    '&::-webkit-scrollbar': { width: 6 },
                    '&::-webkit-scrollbar-track': { backgroundColor: 'rgba(0, 0, 0, 0.05)' },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: 'rgba(0, 0, 0, 0.2)',
                      borderRadius: 3
                    },
                  }}
                  role="list"
                  aria-label="Transcript turns"
                >
                  {isSampleSession ? (
                    <EmptyOrLoadingState message="The transcript is not available for sample sessions" />
                  ) : turns.length > 0 ? (
                    turns.map((turn, turnIndex) => {
                      const inRange = isTurnInSelectedRange(turn);
                      const inInnerContext = isTurnInInnerContext(turn);
                      const borderColor = getBorderColor(selectedFeedback, inRange);
                      const combinedText = turn.texts.join(' ');

                      return (
                        <React.Fragment key={`turn-${turn.startIndex}`}>
                          <ListItem
                            data-turn-index={turnIndex}
                            data-turn-start-index={turn.startIndex}
                            sx={{
                              py: 1.5,
                              px: 2,
                              backgroundColor: inInnerContext && selectedFeedback
                                ? alpha(getCategoryColor(selectedFeedback.classification), 0.25)
                                : turn.speakerType?.toLowerCase() === 'student'
                                  ? COLORS.studentRowBg
                                  : 'transparent',
                              borderLeft: `4px solid ${borderColor}`,
                              borderRight: `4px solid ${borderColor}`,
                              alignItems: 'flex-start',
                              flexDirection: 'column',
                              transition: 'all 0.3s',
                              opacity: selectedFeedback && !inRange ? 0.4 : 1,
                              '&:hover': {
                                backgroundColor: inInnerContext && selectedFeedback
                                  ? alpha(getCategoryColor(selectedFeedback.classification), 0.35)
                                  : turn.speakerType?.toLowerCase() === 'student'
                                    ? COLORS.studentRowHoverBg
                                    : inRange
                                      ? alpha(COLORS.transcriptHoverTint, 0.15)
                                      : alpha(COLORS.iconDark, 0.02)
                              }
                            }}
                            role="listitem"
                            aria-label={`Transcript turn by ${turn.utterances[0]?.speakerPrefix}`}
                          >
                            <ListItemText
                              sx={{
                                m: 0,
                                ml: turn.speakerType?.toLowerCase() === 'student' ? 5 : 0
                              }}
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography variant="body2" sx={{
                                    ...TYPOGRAPHY.transcriptText,
                                    lineHeight: 1.4,
                                    color: 'text.primary'
                                  }}>
                                    {turn.utterances[0]?.speakerPrefix}: {combinedText}
                                  </Typography>
                                </Box>
                              }
                            />
                          </ListItem>

                          {turnIndex < turns.length - 1 && (
                            <Divider sx={{ mx: 2 }} />
                          )}
                        </React.Fragment>
                      );
                    })
                  ) : (
                    <EmptyOrLoadingState message="No transcript data available" />
                  )}
                </List>

              </Paper>
            </Grid>

            {/* Right Column - Selected Feedback */}
            <Grid item xs={12} md={5} sx={{
              height: { xs: '500px', md: '100%' },
              minWidth: '250px'
            }}>
              <Paper elevation={0} sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: COLORS.white }}
                role="region"
                aria-label="Selected feedback">
                <Box sx={{ px: 2, pt: 2, pb: 1, borderBottom: `1px solid ${COLORS.feedbackCardBorder}` }}>
                  <Typography variant="subtitle1" sx={{
                    ...TYPOGRAPHY.title,
                    textAlign: 'left'
                  }}>
                    AI Feedback
                  </Typography>
                </Box>

                <Box sx={{ flex: 1, overflow: 'auto', p: 2 }} aria-labelledby="feedback-heading">
                  {selectedFeedback ? (
                    <>
                      {(() => {
                        const { prev } = getAdjacentFeedbackKeys();
                        return prev ? (
                          <Box sx={{ mb: 2, textAlign: 'center' }}>
                            <KeyboardArrowUpIcon
                              onClick={() => {
                                setSelectedFeedbackKey(prev);
                                const [classification, sentiment] = prev.split('-');
                                mixpanel.track('exploreMyTalkFeedbackSelected', { classification, sentiment, source: 'prev_next_nav' });
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  setSelectedFeedbackKey(prev);
                                  const [classification, sentiment] = prev.split('-');
                                  mixpanel.track('exploreMyTalkFeedbackSelected', { classification, sentiment, source: 'prev_next_nav' });
                                }
                              }}
                              aria-label="View previous feedback item"
                              role='button'
                              tabIndex={0}
                              sx={{
                                color: COLORS.iconDark,
                                cursor: 'pointer',
                                fontSize: '2rem',
                                '&:hover': {
                                  opacity: 0.6
                                }
                              }}
                            />
                          </Box>
                        ) : null;
                      })()}
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2,
                          border: `1px solid ${COLORS.feedbackCardBorder}`,
                          borderRadius: 2,
                          bgcolor: COLORS.white,
                          textAlign: 'left'
                        }}
                      >
                        {/* Title */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                          <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minWidth: '28px',
                            width: '28px',
                            height: '28px',
                            flexShrink: 0
                          }}>
                            {selectedFeedback.sentiment === 'strength' ? (
                              <Flower
                                flowerId={`feedback-flower-${selectedFeedback.classification}`}
                                color={getCategoryFlowerColor(toTitleCase(selectedFeedback.classification))}
                              />
                            ) : (
                              <svg width="28" height="32" viewBox="0 0 90 110" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <g transform="translate(45, 50) rotate(-30) translate(-45, -45)">
                                  <path d="M 68.088 66.685 C 42.441 92.332 26.759 89.373 5.407 81.712 c -7.661 -21.352 -10.62 -37.035 15.027 -62.681 C 42.278 29.721 58.295 45.49 68.088 66.685 z" fill={getCategoryColor(selectedFeedback.classification)} />
                                  <path d="M 74.939 21.703 l -9.802 -9.802 c -12.027 -12.027 -31.527 -12.027 -43.554 0 l 0 0 c -2.286 2.286 -2.286 5.993 0 8.279 L 66.66 65.257 c 2.286 2.286 5.993 2.286 8.279 0 l 0 0 C 86.966 53.23 86.966 33.73 74.939 21.703 z" fill={ACORN_BROWN} />
                                  <path d="M 71.529 18.294 c 5.482 -3.916 11.757 -5.109 18.471 -4.802 l -5.474 -5.474 c -6.117 -0.696 -10.381 2.724 -15.701 7.571 C 67.923 17.426 68.835 18.322 71.529 18.294 z" fill={ACORN_BROWN} />
                                </g>
                              </svg>
                            )}
                          </Box>
                          <Typography variant="h6" sx={TYPOGRAPHY.feedbackTitle}>
                            {toTitleCase(selectedFeedback.classification)} {selectedFeedback.sentiment === 'strength' ? 'Strength' : 'Opportunity'}
                          </Typography>
                        </Box>

                        {/* Feedback Content */}
                        <Typography variant="body1" sx={{
                          ...TYPOGRAPHY.feedbackText,
                          lineHeight: 1.6,
                          mb: 2
                        }}>
                          {selectedFeedback.text}
                        </Typography>

                        {selectedFeedback.contexts && selectedFeedback.contexts.length > 0 && (
                          <>
                            {(() => {
                              const category = CATEGORY_LABEL_MAP[selectedFeedback.classification];

                              return category ? (
                                <Box sx={{ mb: 2 }}>
                                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                    <Chip
                                      label={category}
                                      variant="outlined"
                                      sx={{
                                        backgroundColor: (() => {
                                          const color = getCategoryColor(selectedFeedback.classification);
                                          return color === COLORS.categoryLearningCommunity ? COLORS.chipLearningCommunityBg :
                                            color === COLORS.categoryContentKnowledge ? COLORS.chipContentKnowledgeBg :
                                              color === COLORS.categoryRigorousThinking ? COLORS.chipRigorousThinkingBg :
                                                COLORS.chipDefaultBg;
                                        })(),
                                        color: (() => {
                                          const color = getCategoryColor(selectedFeedback.classification);
                                          return color === COLORS.categoryLearningCommunity ? COLORS.chipLearningCommunityText :
                                            color === COLORS.categoryContentKnowledge ? COLORS.chipContentKnowledgeText :
                                              color === COLORS.categoryRigorousThinking ? COLORS.chipRigorousThinkingText :
                                                COLORS.chipDefaultText;
                                        })(),
                                        borderRadius: '10px',
                                        border: `2px solid ${getCategoryColor(selectedFeedback.classification)}`,
                                        ...TYPOGRAPHY.chip,
                                        height: '28px',
                                        fontWeight: 600,
                                        '& .MuiChip-label': {
                                          px: 1.5,
                                          py: 0.5,
                                        }
                                      }}
                                    />
                                  </Box>
                                </Box>
                              ) : null;
                            })()}
                          </>
                        )}
                      </Paper>

                      {(() => {
                        const { next } = getAdjacentFeedbackKeys();
                        return next ? (
                          <Box sx={{ mt: 2, textAlign: 'center' }}>
                            <KeyboardArrowDownIcon
                              onClick={() => {
                                setSelectedFeedbackKey(next);
                                const [classification, sentiment] = next.split('-');
                                mixpanel.track('exploreMyTalkFeedbackSelected', { classification, sentiment, source: 'prev_next_nav' });
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  setSelectedFeedbackKey(next);
                                  const [classification, sentiment] = next.split('-');
                                  mixpanel.track('exploreMyTalkFeedbackSelected', { classification, sentiment, source: 'prev_next_nav' });
                                }
                              }}
                              aria-label="View next feedback item"
                              role='button'
                              tabIndex={0}
                              sx={{
                                color: COLORS.iconDark,
                                cursor: 'pointer',
                                fontSize: '2rem',
                                '&:hover': {
                                  opacity: 0.6
                                }
                              }}
                            />
                          </Box>
                        ) : null;
                      })()}
                    </>
                  ) : (
                    <EmptyOrLoadingState message="No feedback available for the selected category and type" />
                  )}
                </Box>
              </Paper>
            </Grid>
          </Grid>
        )}
      </Box>
    </Box>
  );
};
export default ExploreMyTalk;